import {
    BLENDMODE_ONE,
    BLENDMODE_ONE_MINUS_SRC_ALPHA,
    BLENDMODE_SRC_ALPHA,
    BLENDEQUATION_ADD,
    CULLFACE_NONE,
    Color,
    BlendState,
    Entity,
    MeshInstance,
    StandardMaterial,
    Vec3,
    Quat
} from 'playcanvas';

import { Element, ElementType } from './element';
import { defaultGlobalClipBox, type GlobalClipBox } from './global-clip-box';

// A selectable scene element representing the global clip box.
// - Owns a pivot Entity for transform manipulation
// - Syncs with the global clip box state (events.clipBox)
// - Draws a wireframe only when selected
class GlobalClipBoxElement extends Element {
    pivot: Entity;
    private wireMat: StandardMaterial;
    private _selected = false;
    private _wireframeVisible = true;  // Wireframe visibility (independent of selection)
    // Allow Selection system to treat this like other selectable elements.
    visible = true;

    constructor() {
        super(ElementType.clipBox);

        this.pivot = new Entity('globalClipBox');
        this.pivot.addComponent('render', {
            type: 'box'
        });
    }

    add() {
        // Wireframe-ish material: we can’t do true wireframe easily with StandardMaterial,
        // but PlayCanvas will render box edges via meshInstance.renderStyle if available.
        const mat = new StandardMaterial();
        mat.diffuse = new Color(0.2, 0.8, 1.0);
        mat.emissive = new Color(0.05, 0.2, 0.25);
        mat.opacity = 1;
        mat.cull = CULLFACE_NONE;
        mat.blendState = new BlendState(
            true,
            BLENDEQUATION_ADD, BLENDMODE_SRC_ALPHA, BLENDMODE_ONE_MINUS_SRC_ALPHA,
            BLENDEQUATION_ADD, BLENDMODE_ONE, BLENDMODE_ONE_MINUS_SRC_ALPHA
        );
        mat.depthTest = true;
        mat.depthWrite = false;
        mat.update();
        this.wireMat = mat;

        // Put on overlay layer so it stays visible (and doesn't fight the grid/debug pass).
        // Only enabled while selected.
        this.pivot.render.layers = [this.scene.overlayLayer.id];

        // Apply material
        const mi = this.pivot.render.meshInstances?.[0] as MeshInstance;
        if (mi) {
            mi.material = mat;
            // @ts-ignore - not in typings in this repo
            mi.renderStyle = 1; // RENDERSTYLE_WIREFRAME in PlayCanvas
        }

        this.scene.contentRoot.addChild(this.pivot);

        // Keep selection state
        this.scene.events.on('selection.changed', (sel: any) => {
            this._selected = sel === this;
            // Wireframe visibility is independent of selection
            this.pivot.enabled = this._wireframeVisible;
            this.scene.forceRender = true;
        });

        // Wireframe visibility toggle (independent of selection)
        this.scene.events.on('clipBox.wireframe', (visible: boolean) => {
            this._wireframeVisible = visible;
            // Update pivot visibility (independent of selection)
            this.pivot.enabled = this._wireframeVisible;
            this.scene.forceRender = true;
        });

        // Reset/fit clip box to contain all scene content
        this.scene.events.on('clipBox.fitAll', () => {
            const clip = (this.scene.events.invoke('clipBox') ?? defaultGlobalClipBox()) as GlobalClipBox;
            const elements = this.scene.getElementsByType(ElementType.splat)
            .concat(this.scene.getElementsByType(ElementType.model));

            if (elements.length === 0) return;

            // Calculate combined bounding box
            let minX = Infinity, minY = Infinity, minZ = Infinity;
            let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

            elements.forEach((element: any) => {
                const bound = element.worldBound || element.localBound;
                if (bound) {
                    const c = bound.center;
                    const h = bound.halfExtents;
                    minX = Math.min(minX, c.x - h.x);
                    minY = Math.min(minY, c.y - h.y);
                    minZ = Math.min(minZ, c.z - h.z);
                    maxX = Math.max(maxX, c.x + h.x);
                    maxY = Math.max(maxY, c.y + h.y);
                    maxZ = Math.max(maxZ, c.z + h.z);
                }
            });

            if (isFinite(minX)) {
                const center = new Vec3(
                    (minX + maxX) * 0.5,
                    (minY + maxY) * 0.5,
                    (minZ + maxZ) * 0.5
                );
                const halfExtents = new Vec3(
                    (maxX - minX) * 0.5 * 1.2,  // 20% padding
                    (maxY - minY) * 0.5 * 1.2,
                    (maxZ - minZ) * 0.5 * 1.2
                );
                // IMPORTANT: Do not implicitly enable the clip box. Users should toggle it on explicitly.
                // Preserve the current enabled state.
                this.scene.events.fire('clipBox.set', { center, halfExtents, enabled: clip.enabled });
            }
        });

        // Keep transform in sync with clipBox state
        const syncFromClip = (clipBox: GlobalClipBox) => {
            // pivot uses full size, clip box stores halfExtents
            this.pivot.setPosition(clipBox.center);
            this.pivot.setRotation(clipBox.rotation);
            this.pivot.setLocalScale(
                clipBox.halfExtents.x * 2,
                clipBox.halfExtents.y * 2,
                clipBox.halfExtents.z * 2
            );
        };

        this.scene.events.on('clipBox.changed', (clipBox: GlobalClipBox) => {
            syncFromClip(clipBox);
        });

        // initialize
        syncFromClip(defaultGlobalClipBox());
        this.pivot.enabled = this._wireframeVisible;
    }

    remove() {
        this.scene.contentRoot.removeChild(this.pivot);
    }

    // Called by transform tools / transform handler
    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        const clip = (this.scene.events.invoke('clipBox') ?? defaultGlobalClipBox()) as GlobalClipBox;

        const next: Partial<GlobalClipBox> = {};
        if (position) next.center = position.clone();
        if (rotation) next.rotation = rotation.clone();
        if (scale) {
            // scale from pivot is full extents, convert to halfExtents
            // Clamp to minimum to prevent zero/near-zero dimensions
            const MIN_HALF_EXTENT = 0.01;
            next.halfExtents = new Vec3(
                Math.max(MIN_HALF_EXTENT, Math.abs(scale.x) * 0.5),
                Math.max(MIN_HALF_EXTENT, Math.abs(scale.y) * 0.5),
                Math.max(MIN_HALF_EXTENT, Math.abs(scale.z) * 0.5)
            );
        }

        // IMPORTANT: Transform edits must not implicitly enable the clip box.
        // Preserve enabled state unless the user explicitly toggles it.
        next.enabled = clip.enabled;
        this.scene.events.fire('clipBox.set', next);
    }
}

export { GlobalClipBoxElement };
